import {
  deserify,
  getFileExtensionType,
  getFileSizeInMB,
  getFileType,
  getImageType,
  getMediaBasePath,
  IStyles,
  IconWrapper,
  pxToRem,
  pxTovW,
  QuantityButtonV2,
  SelectMenu,
  uploadFileV2,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import {
  Checkbox,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { FileUploadPurpose } from '@protos/learning_management/lms.common.apis_pb';
import { CloudUpload } from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { DragEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setToastInfo } from '../../../../Home/reducer/homeDashboard.slice';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import {
  formatFileSize,
  getCloudUploadPreviewSrc,
} from '../../../../../utils/commonUtils';
import {
  composePassageTextWithInstruction,
  countWords,
  formatMarksDisplay,
  getDefaultPassageInstruction,
  hasPassageInstructionSeparator,
  MIN_PASSAGE_WORD_COUNT,
  replacePassageInstructionQRange,
  splitPassageTextWithInstruction,
} from '../../../utils/QPGv2.utils';

const ACTIVE_BLUE = '#007CDC';

const styles: IStyles = {
  root: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#F6FFFA',
    border: '1px solid #55ACFF',
    borderRadius: { xs: pxToRem(12), md: pxTovW(16) },
    padding: { xs: pxToRem(12), md: pxTovW(16) },
    marginTop: { xs: pxToRem(12), md: pxTovW(14) },
    marginBottom: { xs: pxToRem(12), md: pxTovW(16) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(12), md: pxTovW(14) },
  },
  sourceRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
  },
  sourceButtonActive: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'pointer',
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(8) },
    paddingY: { xs: pxToRem(6), md: pxTovW(8) },
    fontFamily: 'Poppins',
    border: `1px solid ${ACTIVE_BLUE}`,
    backgroundColor: ACTIVE_BLUE,
    color: '#fff',
  },
  sourceButtonInactive: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'pointer',
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(8) },
    paddingY: { xs: pxToRem(6), md: pxTovW(8) },
    fontFamily: 'Poppins',
    border: `1px solid ${ACTIVE_BLUE}`,
    backgroundColor: '#fff',
    color: ACTIVE_BLUE,
  },
  sourceButtonIcon: {
    fontSize: { xs: 16, md: 18 },
    lineHeight: 1,
  },
  sourceButtonLabel: {
    color: 'inherit',
    fontWeight: 500,
  },
  multiSelectParentStyles: {
    width: { xs: '100%', md: pxTovW(220), lg: pxTovW(180) },
    height: { xs: pxToRem(36), md: pxTovW(40), lg: pxTovW(40) },
    lineHeight: 0,
  },
  multiSelectRoot: {
    width: { xs: '100%', md: pxTovW(220), lg: pxTovW(180) },
    height: { xs: pxToRem(36), md: pxTovW(40), lg: pxTovW(40) },
    minHeight: { xs: pxToRem(36), md: pxTovW(40) },
    maxHeight: { xs: pxToRem(36), md: pxTovW(40) },
    boxSizing: 'border-box',
    padding: 0,
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    overflow: 'hidden',
    background: '#fff',
    backgroundColor: '#fff',
    border: '1px solid #55ACFF',
    fontSize: { xs: pxToRem(12), md: pxTovW(13) },
    '.MuiOutlinedInput-notchedOutline': {
      border: 'none',
      top: 0,
      legend: { display: 'none' },
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&.MuiInputBase-root': {
      height: { xs: pxToRem(36), md: pxTovW(40) },
      minHeight: { xs: pxToRem(36), md: pxTovW(40) },
      padding: 0,
    },
    '& .MuiSelect-select': {
      height: '100%',
      minHeight: 'unset',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: { xs: pxToRem(10), md: pxTovW(12) },
      paddingRight: { xs: pxToRem(28), md: pxTovW(32) },
    },
  },
  multiSelectValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
    minWidth: 0,
    width: '100%',
  },
  aiInfoEmoji: {
    fontSize: 16,
    lineHeight: 1,
  },
  aiInfoText: {
    color: '#4A5568',
    fontWeight: 400,
  },
  imageBoxWithPreview: {
    width: '100%',
    flex: { xs: '0 0 auto', md: 1 },
    minHeight: { xs: pxToRem(85), md: 0 },
    boxSizing: 'border-box',
    border: '1.5px dashed #55ACFF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(12) },
    backgroundColor: '#DCFAEF',
    display: 'block',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'default',
    padding: 0,
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  imageBoxUploading: {
    width: '100%',
    flex: { xs: '0 0 auto', md: 1 },
    minHeight: { xs: pxToRem(85), md: 0 },
    boxSizing: 'border-box',
    border: '1.5px dashed #55ACFF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(12) },
    backgroundColor: '#DCFAEF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'default',
    padding: { xs: pxToRem(12), md: pxTovW(14) },
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  closeIconSmall: {
    fontSize: 10,
  },
  closeIconMedium: {
    fontSize: 16,
  },
  attemptAnyRow: {
    display: 'flex',
    flexWrap: { xs: 'wrap', md: 'nowrap' },
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
    flexGrow: 1,
  },
  attemptAnyCheckbox: {
    padding: 0,
    color: ACTIVE_BLUE,
    '&.Mui-checked': { color: ACTIVE_BLUE },
  },
  attemptAnyLabel: {
    cursor: 'pointer',
  },
  attemptAnyControls: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxTovW(30) },
  },
  marksPerQuestionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(6) },
    flexBasis: { xs: '100%', md: 'auto' },
    width: { xs: '100%', md: 'auto' },
    ml: { xs: 0, md: pxTovW(24) },
  },
  attemptAnyHelperText: {
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  chapterTopicSelects: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: { xs: pxToRem(8), md: pxTovW(5) },
    flex: { xs: '1 1 100%', md: '1 1 auto' },
    minWidth: 0,
  },

  multiSelectValue: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
    fontSize: { xs: pxToRem(12), md: pxTovW(13) },
    fontFamily: 'Poppins',
    color: '#1D1D1D',
  },
  multiSelectPlaceholder: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 500,
    fontSize: { xs: pxToRem(12), md: pxTovW(13) },
    fontFamily: 'Poppins',
    color: '#6B7A90',
  },
  checkboxMenuItem: {
    borderBottom: '1px solid #D6ECFF',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: '#EAF6FF' },
    paddingX: { xs: pxToRem(10), md: pxTovW(12) },
    paddingY: { xs: pxToRem(8), md: pxTovW(10) },
    display: 'flex',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    alignItems: 'center',
    backgroundColor: '#F5FAFF',
    width: '100%',
  },
  checkboxStyle: {
    padding: 0,
    color: ACTIVE_BLUE,
    '&.Mui-checked': { color: ACTIVE_BLUE },
  },
  checkboxMenuItemTypo: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  moreCountChip: {
    backgroundColor: ACTIVE_BLUE,
    borderRadius: { xs: pxToRem(10), md: pxTovW(12) },
    paddingX: { xs: pxToRem(6), md: pxTovW(8) },
    paddingY: { xs: pxToRem(2), md: pxTovW(2) },
    color: '#fff',
    fontWeight: 600,
    fontSize: { xs: pxToRem(10), md: pxTovW(11) },
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  aiInfoBox: {
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(8), md: pxTovW(10) },
    backgroundColor: '#fff',
    border: '1px dashed #55ACFF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
    paddingX: { xs: pxToRem(10), md: pxTovW(12) },
    paddingY: { xs: pxToRem(8), md: pxTovW(10) },
  },
  editorRow: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: 'stretch',
    gap: { xs: pxToRem(12), md: pxTovW(16) },
  },
  instructionField: {
    width: '100%',
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff',
      borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
      border: '1px solid #D0D5DD',
      color: '#6B7A90',
      '&:hover': {
        border: '1px solid #D0D5DD',
      },
      '&.Mui-focused': {
        border: '1px solid #D0D5DD',
      },
      '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputBase-input': {
      color: '#6B7A90',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#9AA5B5',
      opacity: 1,
    },
  },
  textField: {
    width: { xs: '100%' },
    flex: { xs: '0 0 auto', md: '7 1 0' },
    minWidth: 0,
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      minHeight: { xs: pxToRem(115), md: pxTovW(115) },
      backgroundColor: '#fff',
      borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
      border: '1px solid #55ACFF',
      '&:hover': {
        border: '1px solid #55ACFF',
      },
      '&.Mui-focused': {
        border: '1px solid #55ACFF',
      },
      '& fieldset, &:hover fieldset, &.Mui-focused fieldset': {
        border: 'none',
      },
    },
  },
  imageColumn: {
    width: { xs: '100%' },
    flex: { xs: '0 0 auto', md: '3 1 0' },
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
  },
  imageLabel: {
    color: '#1D1D1D',
    fontWeight: 600,
  },
  fileInfoDetailsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(2), md: pxTovW(4) },
    width: '100%',
    alignItems: 'center',
  },
  fileInfoDetailText: {
    color: '#6B7A90',
    fontWeight: 400,
    fontSize: { xs: pxToRem(11), md: pxTovW(12) },
    textAlign: 'center',
  },
  imageBox: {
    width: '100%',
    flex: { xs: '0 0 auto', md: 1 },
    minHeight: { xs: pxToRem(85), md: 0 },
    boxSizing: 'border-box',
    border: '1.5px dashed #55ACFF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(12) },
    backgroundColor: '#DCFAEF',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    cursor: 'pointer',
    padding: { xs: pxToRem(12), md: pxTovW(14) },
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  imagePreview: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: { xs: pxToRem(6), md: pxTovW(8) },
    display: 'block',
  },
  imageDeleteButton: {
    position: 'absolute',
    top: { xs: pxToRem(4), md: pxTovW(6) },
    right: { xs: pxToRem(4), md: pxTovW(6) },
    width: { xs: pxToRem(24), md: pxTovW(26) },
    height: { xs: pxToRem(24), md: pxTovW(26) },
    padding: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    color: ACTIVE_BLUE,
    boxShadow: '0 1px 4px rgba(0,0,0,0.16)',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#C62828',
    },
  },
  galleryIcon: {
    width: { xs: pxToRem(32), md: pxTovW(36) },
    height: { xs: pxToRem(32), md: pxTovW(36) },
  },
  uploadFileRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(4), md: pxTovW(6) },
    minWidth: 0,
  },
  fileTypeIcon: {
    width: { xs: pxToRem(16), md: pxTovW(16) },
    height: { xs: pxToRem(16), md: pxTovW(16) },
    flexShrink: 0,
    borderRadius: { xs: pxToRem(3), md: pxTovW(3) },
    backgroundColor: '#E53935',
    color: '#fff',
    fontSize: { xs: pxToRem(12), md: pxTovW(12) },
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    color: ACTIVE_BLUE,
    fontWeight: 600,
    fontSize: { xs: pxToRem(10), md: pxTovW(11) },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  fileSize: {
    flexShrink: 0,
    color: '#6B7A90',
    fontSize: { xs: pxToRem(10), md: pxTovW(11) },
    whiteSpace: 'nowrap',
  },
  uploadPercent: {
    flexShrink: 0,
    color: ACTIVE_BLUE,
    fontWeight: 600,
    fontSize: { xs: pxToRem(10), md: pxTovW(11) },
    whiteSpace: 'nowrap',
  },
  cancelUploadButton: {
    width: { xs: pxToRem(16), md: pxTovW(18) },
    height: { xs: pxToRem(16), md: pxTovW(18) },
    padding: 0,
    flexShrink: 0,
    backgroundColor: '#8A94A6',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#6B7A90',
    },
  },
  uploadProgress: {
    width: '100%',
    height: { xs: pxToRem(6), md: pxTovW(6) },
    borderRadius: pxToRem(99),
    backgroundColor: '#C8F0DC',
    '& .MuiLinearProgress-bar': {
      backgroundColor: '#0AA34F',
      borderRadius: pxToRem(99),
    },
  },
  uploadButton: {
    backgroundColor: ACTIVE_BLUE,
    color: '#fff',
    borderRadius: { xs: pxToRem(6), md: pxTovW(8) },
    paddingX: { xs: pxToRem(14), md: pxTovW(16) },
    paddingY: { xs: pxToRem(4), md: pxTovW(5) },
    fontWeight: 700,
    fontSize: { xs: pxToRem(11), md: pxTovW(12) },
    letterSpacing: '0.04em',
    fontFamily: 'Poppins',
  },
  settingsRow: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    flexWrap: 'wrap',
    alignItems: { xs: 'flex-start', md: 'flex-end' },
    gap: { xs: pxToRem(12), md: pxTovW(24) },
  },
  marksStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    flexShrink: 0,
  },
  headerMarksPill: {
    backgroundColor: '#DCFAEF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    paddingX: { xs: pxToRem(12), md: pxTovW(15) },
    paddingY: { xs: pxToRem(10), md: pxTovW(5) },
    height: { xs: pxToRem(30), md: pxTovW(45) },
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
  },
  marksPill: {
    backgroundColor: '#DCFAEF',
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
    paddingX: { xs: pxToRem(16), md: pxTovW(20) },
    paddingY: { xs: pxToRem(6), md: pxTovW(8) },
    fontWeight: 700,
    color: '#1D1D1D',
    fontFamily: 'Poppins',
  },
  settingItem: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'column' },
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    alignSelf: 'stretch',
    alignItems: 'flex-start',
  },
  attemptAnyQuantityRoot: {
    width: { xs: pxToRem(100), md: pxTovW(148), lg: pxTovW(132) },
    height: { xs: pxToRem(30), md: pxTovW(42), lg: pxTovW(40) },
    maxWidth: { xs: pxToRem(100), md: 'none' },
    flexShrink: 0,
  },
};

const MAX_FILE_SIZE_MB = 50;
const UPLOAD_ERROR_LABEL = 'Error Occurred! Unable to upload the image.';
const fileInfoDetails = [
  'File Format - jpg, jpeg, png',
  'File size - Maximum 50MB',
];

interface QPGPassageSectionPanelProps {
  sectionMarks: number;
  passageText: string;
  attemptAnyCount?: number;
  maxAttemptAnyCount: number;
  marksPerQuestion: number;
  maxMarksPerQuestion: number;
  questionRangeStart: number;
  questionRangeEnd: number;
  passageImagesUpload: CloudUpload[];
  isAiGeneratedPassage: boolean;
  passageModuleIds: number[];
  onPassageTextChange: (text: string) => void;
  onAttemptAnyCountChange: (count: number | undefined) => void;
  onMarksPerQuestionChange: (marks: number) => void;
  onPassageImagesChange: (images: CloudUpload[]) => void;
  onIsAiGeneratedPassageChange: (isAiGenerated: boolean) => void;
  onPassageModuleIdsChange: (moduleIds: number[]) => void;
}

export const QPGPassageSectionPanel = ({
  sectionMarks,
  passageText,
  attemptAnyCount,
  maxAttemptAnyCount,
  marksPerQuestion,
  maxMarksPerQuestion,
  questionRangeStart,
  questionRangeEnd,
  passageImagesUpload,
  isAiGeneratedPassage,
  passageModuleIds,
  onPassageTextChange,
  onAttemptAnyCountChange,
  onMarksPerQuestionChange,
  onPassageImagesChange,
  onIsAiGeneratedPassageChange,
  onPassageModuleIdsChange,
}: QPGPassageSectionPanelProps) => {
  const dispatch = useDispatch();
  const sectionMarksDisplay = formatMarksDisplay(sectionMarks);
  const { LmsCommonAPIServiceV2ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { qpg_content, qpg_selected_chapter_data, qpg_selected_section_index } =
    deserify(useAppSelector((state) => state.assessment));
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const didPreselectModulesRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const effectiveRangeStart = Math.max(1, questionRangeStart || 1);
  const effectiveRangeEnd = Math.max(
    effectiveRangeStart,
    questionRangeEnd || effectiveRangeStart
  );
  const { instruction: storedInstruction, body: passageBody } =
    splitPassageTextWithInstruction(passageText);
  const displayInstruction = storedInstruction;
  const passageWordCount = countWords(passageBody);
  const isPassageBelowMinLength =
    !isAiGeneratedPassage && passageWordCount < MIN_PASSAGE_WORD_COUNT;
  const abortControllerRef = useRef<AbortController | null>(null);
  const isAttemptAny = attemptAnyCount !== undefined;
  const canEnableAttemptAny = maxAttemptAnyCount >= 1;
  const uploadedImage = passageImagesUpload[0];
  const persistedPreviewKey =
    uploadedImage?.gcpFileUrl ||
    uploadedImage?.previewUrl ||
    uploadedImage?.fileName ||
    '';
  const imagePreviewSrc =
    localPreviewUrl || getCloudUploadPreviewSrc(uploadedImage);

  const updateInstruction = (nextInstruction: string) => {
    onPassageTextChange(
      composePassageTextWithInstruction({
        instruction: nextInstruction,
        body: passageBody,
      })
    );
  };

  const updatePassageBody = (nextBody: string) => {
    onPassageTextChange(
      composePassageTextWithInstruction({
        instruction: storedInstruction,
        body: nextBody,
      })
    );
  };

  const availableChapters = qpg_selected_chapter_data?.chapters ?? [];

  const chapterOptions = useMemo(
    () =>
      availableChapters.map((chapter) => ({
        id: chapter.chapterId,
        name: `${chapter.chapterNo}. ${chapter.chapterTitle}`,
        topics: chapter.topics ?? [],
      })),
    [availableChapters]
  );

  const allAvailableModuleIds = useMemo(
    () =>
      chapterOptions.flatMap((chapter) => [
        chapter.id,
        ...chapter.topics.map((topic) => topic.topicId),
      ]),
    [chapterOptions]
  );

  // Includes topic ids so topic edits on Step 1 also trigger a full reseed.
  const availableModulesKey = useMemo(
    () =>
      chapterOptions
        .map(
          (chapter) =>
            `${chapter.id}:${chapter.topics
              .map((topic) => topic.topicId)
              .sort((a, b) => a - b)
              .join('.')}`
        )
        .join('|'),
    [chapterOptions]
  );

  const prevAvailableModulesKeyRef = useRef<string | null>(null);

  const selectedChapterIds = useMemo(
    () =>
      chapterOptions
        .filter((chapter) => passageModuleIds.includes(chapter.id))
        .map((chapter) => chapter.id),
    [chapterOptions, passageModuleIds]
  );

  const topicOptions = useMemo(
    () =>
      chapterOptions
        .filter((chapter) => selectedChapterIds.includes(chapter.id))
        .flatMap((chapter) =>
          chapter.topics.map((topic) => ({
            id: topic.topicId,
            name: `${topic.topicTitle}`,
            chapterId: chapter.id,
          }))
        ),
    [chapterOptions, selectedChapterIds]
  );

  const selectedTopicIds = useMemo(
    () =>
      topicOptions
        .filter((topic) => passageModuleIds.includes(topic.id))
        .map((topic) => topic.id),
    [topicOptions, passageModuleIds]
  );

  const selectedChapterOptions = chapterOptions.filter((chapter) =>
    selectedChapterIds.includes(chapter.id)
  );
  const selectedTopicOptions = topicOptions.filter((topic) =>
    selectedTopicIds.includes(topic.id)
  );

  const chapterDisplayValue =
    selectedChapterOptions.length === 0
      ? ''
      : selectedChapterOptions.length === chapterOptions.length &&
        chapterOptions.length > 0
      ? 'All Chapters'
      : selectedChapterOptions[0]?.name ?? '';

  const topicDisplayValue =
    selectedTopicOptions.length === 0
      ? ''
      : selectedTopicOptions.length === topicOptions.length &&
        topicOptions.length > 0
      ? 'All Topics'
      : selectedTopicOptions[0]?.name ?? '';

  // ------------------------------------------------------------

  const toggleChapterId = (chapterId: number) => {
    const isSelected = selectedChapterIds.includes(chapterId);
    const chapterTopicIds =
      chapterOptions
        .find((chapter) => chapter.id === chapterId)
        ?.topics.map((topic) => topic.topicId) ?? [];

    if (isSelected) {
      const removeIds = new Set([chapterId, ...chapterTopicIds]);
      onPassageModuleIdsChange(
        passageModuleIds.filter((id) => !removeIds.has(id))
      );
      return;
    }

    onPassageModuleIdsChange([
      ...new Set([...passageModuleIds, chapterId, ...chapterTopicIds]),
    ]);
  };

  // ------------------------------------------------------------

  const toggleTopicId = (topicId: number) => {
    if (selectedTopicIds.includes(topicId)) {
      onPassageModuleIdsChange(passageModuleIds.filter((id) => id !== topicId));
      return;
    }
    onPassageModuleIdsChange([...new Set([...passageModuleIds, topicId])]);
  };

  // ------------------------------------------------------------

  // Blob URL for instant post-upload preview only — not the source of truth;
  // persisted image comes from passageImagesUpload via getCloudUploadPreviewSrc.
  const setLocalPreviewFromFile = (file: File) => {
    setLocalPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return URL.createObjectURL(file);
    });
  };

  // ------------------------------------------------------------

  // Revoke blob URL when user removes the image or panel state is reset.
  const clearLocalPreview = () => {
    setLocalPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return '';
    });
  };

  // ------------------------------------------------------------

  const showErrorToast = (label: string) => {
    dispatch(
      setToastInfo({
        label,
        variant: 'error',
        open: true,
      })
    );
  };

  // ------------------------------------------------------------

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ------------------------------------------------------------

  const clearUploadProgress = () => {
    setIsUploading(false);
    setPendingFile(null);
    setUploadPercent(0);
    abortControllerRef.current = null;
    resetFileInput();
  };

  // ------------------------------------------------------------

  const cancelUpload = () => {
    abortControllerRef.current?.abort();
  };

  // ------------------------------------------------------------

  const uploadPassageImage = async (file?: File) => {
    if (!file) return;

    if (
      !file.type.startsWith('image/') ||
      getFileType(file.type) !== FileEnum.FILE_TYPE_IMAGE
    ) {
      showErrorToast('Please add an Image File');
      resetFileInput();
      return;
    }

    const sizeMb = getFileSizeInMB(file.size);
    if (sizeMb > MAX_FILE_SIZE_MB) {
      showErrorToast('File Size should be less than 50 MB');
      resetFileInput();
      return;
    }

    const personId = user_info?.teacherProfileId;
    if (!personId) {
      showErrorToast(UPLOAD_ERROR_LABEL);
      resetFileInput();
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setPendingFile(file);
    setUploadPercent(0);
    setIsUploading(true);

    try {
      const signedUrlResponse =
        await LmsCommonAPIServiceV2ClientWithStatusCodeHandler.getSignedUrlForFileUpload(
          {
            personId,
            filesInfo: [
              {
                originalFilePath: file.name,
                mimeType: getImageType(file.type),
                size: Math.max(0.01, sizeMb),
              },
            ],
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            fileUploadPurpose: FileUploadPurpose.FILE_UPLOAD_TEACHER_QUESTION,
            sectionId: qpg_content?.assessment?.sectionId,
            subjectId:
              qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0]
                ?.subjectId,
            schoolId: user_info?.schoolDetails?.[0]?.schoolId,
          }
        );

      if (controller.signal.aborted) return;

      const rawSignedUrl = signedUrlResponse?.response?.[0]?.signedUrl;
      if (!rawSignedUrl) {
        showErrorToast(UPLOAD_ERROR_LABEL);
        return;
      }

      const uploadSuccess = await uploadFileV2({
        file,
        signedUrl: getMediaBasePath(rawSignedUrl),
        onProgress: setUploadPercent,
        signal: controller.signal,
      });
      if (uploadSuccess.aborted) return;
      if (!uploadSuccess.success) {
        showErrorToast(UPLOAD_ERROR_LABEL);
        return;
      }

      setLocalPreviewFromFile(file);
      onPassageImagesChange([
        new CloudUpload({
          fileName: file.name,
          personId,
          fileType: getFileType(file.type),
          fileExtensionType: getFileExtensionType(file.type),
          fileSizeInMb: sizeMb,
          gcpFileUrl: rawSignedUrl,
          // signedUrl: rawSignedUrl,
          // previewUrl: objectPath,
          isUploaded: true,
          isDeleted: false,
        }),
      ]);
    } catch {
      if (controller.signal.aborted) return;
      showErrorToast(UPLOAD_ERROR_LABEL);
    } finally {
      clearUploadProgress();
    }
  };

  // ------------------------------------------------------------

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isUploading || imagePreviewSrc) return;
    void uploadPassageImage(event.dataTransfer.files?.[0]);
  };

  // ============================================================

  // Keep the instruction line in sync with subsection question numbers.
  // Stored format is: instruction + "\n\n" + passage body.
  useEffect(() => {
    // No "\n\n" yet → brand-new or old passage with only body text.
    // Insert the default instruction (does not overwrite an intentional clear,
    // which is stored as "\n\n" + body and already has the separator).
    if (!hasPassageInstructionSeparator(passageText)) {
      const nextStored = composePassageTextWithInstruction({
        instruction: getDefaultPassageInstruction({
          start: effectiveRangeStart,
          end: effectiveRangeEnd,
        }),
        body: passageBody,
      });
      if (nextStored !== passageText) {
        onPassageTextChange(nextStored);
      }
      return;
    }

    // Separator present but instruction text is empty → user cleared it; leave empty.
    if (!storedInstruction) {
      return;
    }

    // Update only the "( Qn - Qm )" token when the subsection range changes.
    // Custom wording around that token is preserved.
    const nextInstruction = replacePassageInstructionQRange({
      instruction: storedInstruction,
      start: effectiveRangeStart,
      end: effectiveRangeEnd,
    });
    const nextStored = composePassageTextWithInstruction({
      instruction: nextInstruction,
      body: passageBody,
    });
    if (nextStored !== passageText) {
      onPassageTextChange(nextStored);
    }
    // onPassageTextChange / body / instruction omitted on purpose to avoid loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRangeStart, effectiveRangeEnd, passageText]);

  // ------------------------------------------------------------

  // Pre-select chapter/topic module ids from Step 1 for this passage panel.
  useEffect(() => {
    if (allAvailableModuleIds.length === 0) {
      prevAvailableModulesKeyRef.current = availableModulesKey;
      return;
    }

    const prevKey = prevAvailableModulesKeyRef.current;
    prevAvailableModulesKeyRef.current = availableModulesKey;

    // Step 1 selection changed → reset this panel to the full new chapter/topic set.
    if (prevKey !== null && prevKey !== availableModulesKey) {
      didPreselectModulesRef.current = true;
      onPassageModuleIdsChange(allAvailableModuleIds);
      return;
    }

    // First time this panel has no modules → select everything from Step 1 once.
    if (passageModuleIds.length === 0 && !didPreselectModulesRef.current) {
      didPreselectModulesRef.current = true;
      onPassageModuleIdsChange(allAvailableModuleIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when Step 1 set or empty ids change
  }, [availableModulesKey, allAvailableModuleIds, passageModuleIds.length]);

  // ------------------------------------------------------------

  // Drop any temporary blob preview when the saved upload changes
  // (e.g. subsection reorder reuses this component instance).
  useEffect(() => {
    setLocalPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return '';
    });
  }, [persistedPreviewKey, passageImagesUpload.length]);

  // ------------------------------------------------------------

  // Revoke blob URLs on cleanup so they do not leak in memory.
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  // ============================================================

  return (
    <Box
      sx={styles.root}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={styles.sourceRow}>
        <Box
          sx={
            !isAiGeneratedPassage
              ? styles.sourceButtonActive
              : styles.sourceButtonInactive
          }
          onClick={() => onIsAiGeneratedPassageChange(false)}
        >
          <Typography component="span" sx={styles.sourceButtonIcon}>
            ✍️
          </Typography>
          <Typography variant="h5" sx={styles.sourceButtonLabel}>
            I&apos;ll write the passage myself
          </Typography>
        </Box>
        <Box
          sx={
            isAiGeneratedPassage
              ? styles.sourceButtonActive
              : styles.sourceButtonInactive
          }
          onClick={() => onIsAiGeneratedPassageChange(true)}
        >
          <Typography component="span" sx={styles.sourceButtonIcon}>
            ✨
          </Typography>
          <Typography variant="h5" sx={styles.sourceButtonLabel}>
            Generate from textbook
          </Typography>
        </Box>

        <Box sx={styles.chapterTopicSelects}>
          <SelectMenu
            placeholder="Select Chapters"
            value={chapterDisplayValue}
            optionList={[]}
            onChange={() => undefined}
            disabled={chapterOptions.length === 0}
            parentStyles={styles.multiSelectParentStyles}
            rootStyles={styles.multiSelectRoot}
            menuPaperProps={{
              // minWidth: { xs: '95%', md: 'max-content' },
              maxWidth: { xs: '95%', md: '20%' },
            }}
            renderValue={(selected, placeholder) =>
              selected ? (
                <Box sx={styles.multiSelectValueRow}>
                  <Box sx={styles.multiSelectValue}>{selected}</Box>
                  {selectedChapterOptions.length > 1 &&
                  !(selectedChapterOptions.length === chapterOptions.length) ? (
                    <Box sx={styles.moreCountChip}>
                      +{selectedChapterOptions.length - 1}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box sx={styles.multiSelectPlaceholder}>{placeholder}</Box>
              )
            }
            menuNode={
              <>
                {chapterOptions.length === 0 ? (
                  <MenuItem disabled sx={styles.checkboxMenuItem}>
                    <Typography variant="h5" sx={styles.checkboxMenuItemTypo}>
                      No chapters selected in Step 1
                    </Typography>
                  </MenuItem>
                ) : (
                  chapterOptions.map((chapter) => (
                    <MenuItem
                      key={chapter.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleChapterId(chapter.id);
                      }}
                      sx={styles.checkboxMenuItem}
                    >
                      <Checkbox
                        checked={selectedChapterIds.includes(chapter.id)}
                        sx={styles.checkboxStyle}
                      />
                      <Typography
                        variant="h5"
                        sx={styles.checkboxMenuItemTypo}
                        fontWeight={
                          selectedChapterIds.includes(chapter.id)
                            ? 'bold'
                            : 'regular'
                        }
                      >
                        {chapter.name}
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </>
            }
          />

          <SelectMenu
            placeholder="Select Topics"
            value={topicDisplayValue}
            optionList={[]}
            onChange={() => undefined}
            disabled={
              selectedChapterIds.length === 0 || topicOptions.length === 0
            }
            parentStyles={styles.multiSelectParentStyles}
            rootStyles={styles.multiSelectRoot}
            menuPaperProps={{
              // minWidth: { xs: '95%', md: 'max-content' },
              maxWidth: { xs: '95%', md: '20%' },
            }}
            renderValue={(selected, placeholder) =>
              selected ? (
                <Box sx={styles.multiSelectValueRow}>
                  <Box sx={styles.multiSelectValue}>{selected}</Box>
                  {selectedTopicOptions.length > 1 &&
                  !(selectedTopicOptions.length === topicOptions.length) ? (
                    <Box sx={styles.moreCountChip}>
                      +{selectedTopicOptions.length - 1}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box sx={styles.multiSelectPlaceholder}>{placeholder}</Box>
              )
            }
            menuNode={
              <>
                {topicOptions.length === 0 ? (
                  <MenuItem disabled sx={styles.checkboxMenuItem}>
                    <Typography variant="h5" sx={styles.checkboxMenuItemTypo}>
                      {selectedChapterIds.length === 0
                        ? 'Select chapters first'
                        : 'No topics available'}
                    </Typography>
                  </MenuItem>
                ) : (
                  topicOptions.map((topic) => (
                    <MenuItem
                      key={topic.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTopicId(topic.id);
                      }}
                      sx={styles.checkboxMenuItem}
                    >
                      <Checkbox
                        checked={selectedTopicIds.includes(topic.id)}
                        sx={styles.checkboxStyle}
                      />
                      <Typography
                        variant="h5"
                        sx={styles.checkboxMenuItemTypo}
                        fontWeight={
                          selectedTopicIds.includes(topic.id)
                            ? 'bold'
                            : 'regular'
                        }
                      >
                        {topic.name}
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </>
            }
          />
        </Box>
      </Box>

      {!isAiGeneratedPassage ? (
        <TextField
          sx={styles.instructionField}
          multiline
          minRows={2}
          placeholder="Passage instruction"
          value={displayInstruction}
          onChange={(e) => updateInstruction(e.target.value)}
        />
      ) : null}

      {isAiGeneratedPassage ? (
        <Box sx={styles.aiInfoBox}>
          <Typography component="span" sx={styles.aiInfoEmoji}>
            ✨
          </Typography>
          <Typography variant="h5" sx={styles.aiInfoText}>
            AI will read the chapter/textbook selected in Step 1 and generate an
            original passage and questions matching the settings below.
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.editorRow}>
          <TextField
            sx={styles.textField}
            multiline
            minRows={5}
            placeholder={`Type or Paste the Passage / Comprehension Text Here (minimum ${MIN_PASSAGE_WORD_COUNT} words)...`}
            value={passageBody}
            onChange={(e) => updatePassageBody(e.target.value)}
            error={isPassageBelowMinLength && passageBody.trim().length > 0}
            // helperText={
            //   isPassageBelowMinLength
            //     ? `Minimum ${MIN_PASSAGE_WORD_COUNT} words required (${passageWordCount}/${MIN_PASSAGE_WORD_COUNT})`
            //     : undefined
            // }
            helperText={
              isPassageBelowMinLength
                ? `Minimum ${MIN_PASSAGE_WORD_COUNT} words required`
                : undefined
            }
          />
          <Box sx={styles.imageColumn}>
            <Typography variant="h5" sx={styles.imageLabel}>
              Attach Image (optional)
            </Typography>
            <Box
              sx={
                imagePreviewSrc && !isUploading
                  ? styles.imageBoxWithPreview
                  : isUploading
                  ? styles.imageBoxUploading
                  : styles.imageBox
              }
              onClick={() => {
                if (!imagePreviewSrc && !isUploading) {
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e: DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={handleDrop}
            >
              {isUploading && pendingFile ? (
                <>
                  <IconWrapper
                    name="gallery_colorful"
                    type="png"
                    parentFolder="icons"
                    customSx={styles.galleryIcon}
                  />
                  <Box sx={styles.uploadFileRow}>
                    <ImageIcon sx={styles.fileTypeIcon} />
                    <Typography component="span" sx={styles.fileName}>
                      {pendingFile.name}
                    </Typography>
                    <Typography component="span" sx={styles.fileSize}>
                      {formatFileSize(pendingFile.size)}
                    </Typography>
                    <Typography component="span" sx={styles.uploadPercent}>
                      {uploadPercent}%
                    </Typography>
                    <IconButton
                      aria-label="Cancel upload"
                      size="small"
                      sx={styles.cancelUploadButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelUpload();
                      }}
                    >
                      <CloseIcon sx={styles.closeIconSmall} />
                    </IconButton>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadPercent}
                    sx={styles.uploadProgress}
                  />
                  <Box sx={styles.uploadButton}>UPLOAD</Box>
                </>
              ) : imagePreviewSrc ? (
                <>
                  <Box
                    component="img"
                    src={imagePreviewSrc}
                    alt={uploadedImage?.fileName || 'Attached passage image'}
                    sx={styles.imagePreview}
                  />
                  <IconButton
                    aria-label="Remove uploaded image"
                    size="small"
                    sx={styles.imageDeleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      clearLocalPreview();
                      onPassageImagesChange([]);
                      resetFileInput();
                    }}
                  >
                    <CloseIcon sx={styles.closeIconMedium} />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconWrapper
                    name="gallery_colorful"
                    type="png"
                    parentFolder="icons"
                    customSx={styles.galleryIcon}
                  />
                  <Typography variant="h5" color="#6B7A90" fontWeight={400}>
                    Drag & Drop Files Or Upload
                  </Typography>
                  <Box
                    sx={styles.uploadButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    UPLOAD
                  </Box>
                  <Box sx={styles.fileInfoDetailsBox}>
                    {fileInfoDetails.map((detail) => (
                      <Typography
                        key={detail}
                        variant="h5"
                        sx={styles.fileInfoDetailText}
                      >
                        {detail}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                hidden
                onChange={(e) => uploadPassageImage(e.target.files?.[0])}
              />
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={styles.settingsRow}>
        <Box sx={styles.marksStack}>
          <Typography variant="h5" fontWeight={500}>
            Total Marks for this Sub Section
          </Typography>
          {/* <Typography component="span" sx={styles.marksPill}>
            {sectionMarks === 1 ? '1 Mark' : `${sectionMarks} Marks`}
          </Typography> */}

          <Box sx={styles.headerMarksPill}>
            <Typography variant="h3" fontWeight={'bold'}>
              {sectionMarksDisplay === '1'
                ? '1 Mark'
                : `${sectionMarksDisplay} Marks`}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.settingItem}>
          <Box sx={styles.attemptAnyRow}>
            <Checkbox
              checked={isAttemptAny && canEnableAttemptAny}
              disabled={!canEnableAttemptAny}
              onChange={(e) => {
                if (!canEnableAttemptAny) return;
                onAttemptAnyCountChange(e.target.checked ? 1 : undefined);
              }}
              sx={{
                ...styles.attemptAnyCheckbox,
                ...(!canEnableAttemptAny ? { opacity: 0.45 } : {}),
              }}
            />
            <Typography
              variant="h5"
              fontWeight={600}
              onClick={() => {
                if (!canEnableAttemptAny) return;
                onAttemptAnyCountChange(isAttemptAny ? undefined : 1);
              }}
              sx={{
                ...styles.attemptAnyLabel,
                ...(!canEnableAttemptAny
                  ? { cursor: 'default', opacity: 0.45 }
                  : {}),
              }}
            >
              Attempt any
            </Typography>
            {isAttemptAny && canEnableAttemptAny ? (
              <>
                <Box sx={styles.attemptAnyControls}>
                  <QuantityButtonV2
                    size="compact"
                    showStepperWhenZero
                    value={Math.min(
                      Math.max(attemptAnyCount ?? 1, 1),
                      maxAttemptAnyCount
                    )}
                    min={1}
                    max={maxAttemptAnyCount}
                    onChange={(nextValue) =>
                      onAttemptAnyCountChange(
                        Math.min(nextValue, maxAttemptAnyCount)
                      )
                    }
                    rootStyles={styles.attemptAnyQuantityRoot}
                  />
                </Box>
                <Box sx={styles.marksPerQuestionRow}>
                  <Typography variant="h5" fontWeight={600}>
                    Marks per question
                  </Typography>
                  <QuantityButtonV2
                    size="compact"
                    showStepperWhenZero
                    value={Math.min(
                      Math.max(marksPerQuestion, 1),
                      maxMarksPerQuestion
                    )}
                    min={1}
                    max={maxMarksPerQuestion}
                    onChange={(nextValue) =>
                      onMarksPerQuestionChange(
                        Math.min(Math.max(nextValue, 1), maxMarksPerQuestion)
                      )
                    }
                    rootStyles={styles.attemptAnyQuantityRoot}
                  />
                </Box>
              </>
            ) : null}
          </Box>

          <Typography
            variant="caption"
            color="#6B7A90"
            sx={styles.attemptAnyHelperText}
          >
            ( optional leave unchecked to require all questions )
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
